// Article « Livreurs en scooter : quand la vitesse devient un danger »
// Article de sensibilisation à destination des livreurs deux-roues, des
// formateurs ECSR et du grand public. Approche pédagogique : prise de
// conscience plutôt que cours magistral.

import {
  AlertTriangle, Clock, Zap, ShieldCheck, Eye, Brain,
  HeartPulse, MessageSquare, Target, Bike, Cloud, MapPin,
  Lightbulb, Users, Timer, XCircle, CheckCircle2,
} from 'lucide-react';

export const livreursArticle = {
  slug: 'livreurs-scooter-vitesse-danger',
  weekVolume: null,
  weekYear: 2026,
  monthLabel: 'Dossier société',
  category: 'Sécurité & société',
  isGuide: true,
  shortTitle: 'Livreurs : vitesse = danger',
  title: 'Livreurs en scooter : quand la vitesse devient un danger',
  kicker: 'Dossier sensibilisation — risques deux-roues',
  subtitle:
    "Délais imposés, rendement, fatigue : le quotidien d'un livreur en scooter est un cocktail explosif. Décryptage d'une réalité urbaine où la moindre seconde gagnée peut tout coûter.",
  heroImage: '/images/articles/livreur-scooter-poignee.jpg?v=20260514',
  heroImageAlt:
    "Main gantée d'un livreur sur la poignée d'accélération d'un scooter, ambiance urbaine nocturne — symbole du quotidien à risque des livreurs deux-roues",
  topRightKicker: { line1: 'Dossier ECSR', line2: 'Risques métiers' },
  readTimeMin: 5,
  seoDescription:
    "Livreurs en scooter : pourquoi la vitesse devient un piège. Pression du rendement, fatigue, distractions… Le dossier ECSR sur les risques du métier et les bons réflexes à adopter.",
  seoKeywords:
    "livreur scooter, sécurité livreur, accident livreur deux-roues, Uber Eats Deliveroo sécurité, risques routiers livreur, fatigue conducteur, pression rendement livraison, code moto livreur, ECSR sensibilisation",
  about:
    'Sécurité routière des livreurs deux-roues, prévention des risques professionnels',
  source: {
    label: 'Sécurité routière + Observatoire National Interministériel de la Sécurité Routière (ONISR)',
    url: 'https://www.onisr.securite-routiere.gouv.fr',
    refs:
      "Bilan annuel ONISR 2024 sur la mortalité deux-roues motorisés ; Code du travail (art. L4121-1 — obligation de sécurité de l'employeur).",
  },

  chapo: {
    kicker: '20 h 30 — sur le terrain',
    body:
      "La ville est saturée. Un scooter zigzague entre les voitures, pressé de livrer une commande. <strong>Cette scène, tu la vois tous les jours.</strong> Mais derrière cette réalité urbaine se cache un cocktail explosif : <strong>vitesse + stress + pression du temps</strong>. Le livreur n'est pas juste un conducteur — c'est un salarié sous contrainte, dont la sécurité passe trop souvent au second plan.",
    sideStat: {
      kicker: 'Le constat',
      big: '×3',
      body:
        "le risque d'accident des livreurs deux-roues par rapport aux autres usagers de la route en milieu urbain (ONISR, 2024). La <strong>pression temporelle</strong> est le premier facteur identifié.",
      accent: '#cf2031',
    },
  },

  pullQuote: {
    text:
      "Un client peut attendre. Un accident, lui, ne prévient pas.",
    source: 'Mantra des moniteurs ECSR — sensibilisation livreurs deux-roues',
  },

  sections: [
    {
      part: 'Partie 1 — Le vrai problème',
      title: "Le livreur n'est pas qu'un conducteur",
      intro:
        "Ce n'est pas le scooter qui est dangereux. C'est <strong>le contexte de travail</strong> qui transforme un trajet anodin en prise de risque. Trois facteurs s'additionnent en permanence.",
      cards: [
        {
          icon: Clock,
          accent: '#cf2031',
          kicker: 'Facteur 1',
          title: 'Délais imposés',
          body:
            "Algorithmes de plateformes, notes clients, primes de rapidité : le livreur est <strong>chronométré en permanence</strong>. Chaque feu rouge, chaque ralentissement devient un manque à gagner ressenti.",
        },
        {
          icon: Target,
          accent: '#f59e0b',
          kicker: 'Facteur 2',
          title: 'Rendement attendu',
          body:
            "Plus de courses = plus de revenus. <strong>Le volume prime sur la prudence</strong>, surtout quand la rémunération est à la course. C'est le piège du modèle économique.",
        },
        {
          icon: HeartPulse,
          accent: '#7c3aed',
          kicker: 'Facteur 3',
          title: 'Fatigue accumulée',
          body:
            "Journées de 8 à 12 h, repas pris sur le scooter, pas de vraies pauses. La <strong>vigilance s'effondre</strong> après 4 h de conduite continue. La fatigue tue autant que l'alcool.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 2 — La physique ne pardonne pas',
      title: "À 45 km/h, qu'est-ce qui se passe vraiment ?",
      intro:
        "On sous-estime toujours la vitesse en ville. Pourtant, à <strong>45 km/h</strong>, un scooter est déjà dans une zone où l'erreur ne pardonne plus.",
      cards: [
        {
          icon: Zap,
          accent: '#0ea5e9',
          kicker: 'Vitesse',
          title: '12,5 mètres / seconde',
          body:
            "À 45 km/h, le scooter parcourt <strong>plus de 12 mètres en 1 seconde</strong>. Une seconde d'inattention (consulter le GPS, regarder une enseigne) = un piéton non vu, une voiture qui freine.",
        },
        {
          icon: Timer,
          accent: '#cf2031',
          kicker: 'Distance d\'arrêt',
          title: '≈ 27 m sur sec',
          body:
            "À 45 km/h, sur sol sec, il faut compter <strong>~ 27 mètres</strong> entre la perception du danger et l'arrêt complet (1 s de réaction + freinage). <strong>Sur sol mouillé : 35 à 40 m.</strong>",
        },
        {
          icon: Cloud,
          accent: '#7c3aed',
          kicker: 'Multiplicateurs',
          title: 'Pluie + nuit + GPS',
          body:
            "Téléphone consulté en roulant, pluie qui réduit l'adhérence, faible luminosité… <strong>Chaque facteur multiplie le risque.</strong> Trois cumulés = situation critique.",
        },
      ],
      cardsCols: 3,
    },

    {
      part: 'Partie 3 — Le rôle du formateur',
      title: 'Ne pas faire un cours. Faire prendre conscience.',
      intro:
        "Les chiffres ne suffisent pas. Pour qu'un livreur change ses comportements, il faut <strong>l'amener à se questionner lui-même</strong>. C'est la méthode pédagogique ECSR.",
      compareCards: [
        {
          accent: '#cf2031',
          kicker: '✗ Ce qui ne marche pas',
          title: 'Le cours magistral',
          intro: "Pourquoi ça échoue avec ce public :",
          points: [
            { ok: false, text: '« Tu dois respecter le Code » → discours déjà entendu 100 fois' },
            { ok: false, text: 'Liste de sanctions → perçue comme une menace, pas un argument' },
            { ok: false, text: "Données statistiques abstraites → « ça n'arrive qu'aux autres »" },
            { ok: false, text: 'Ton moralisateur → fermeture immédiate, rejet du formateur' },
          ],
        },
        {
          accent: '#16a34a',
          kicker: '✓ Ce qui fonctionne',
          title: 'Le questionnement',
          intro: 'Les bonnes questions à poser au livreur :',
          points: [
            { ok: true, text: '<strong>« Pourquoi tu prends ce risque ? »</strong> → fait verbaliser la pression' },
            { ok: true, text: '<strong>« Qu\'est-ce qui compte le plus : livrer vite, ou rentrer vivant ? »</strong>' },
            { ok: true, text: '<strong>« Combien de courses tu fais en plus en grillant ce feu ? »</strong>' },
            { ok: true, text: '<strong>« Qui t\'attend ce soir à la maison ? »</strong>' },
          ],
          footer:
            "Le formateur ne juge pas. Il accompagne le livreur dans une réflexion personnelle.",
        },
      ],
    },

    {
      part: 'Partie 4 — Les 4 réflexes à ancrer',
      title: 'Ce que le livreur doit retenir',
      cards: [
        {
          icon: Eye,
          accent: '#1d4ed8',
          kicker: 'Réflexe 1',
          title: 'Anticiper',
          body:
            "Lire la circulation 5 secondes à l'avance. <strong>Repérer les angles morts</strong> des voitures et camions. Garder une bulle de sécurité autour du scooter, surtout aux intersections.",
        },
        {
          icon: Timer,
          accent: '#16a34a',
          kicker: 'Réflexe 2',
          title: 'Ralentir',
          body:
            "Réduire de 10 km/h en ville fait gagner <strong>quelques secondes par course</strong>, pas plus. Mais ça divise par deux le risque d'accident grave. Le calcul est vite fait.",
        },
        {
          icon: ShieldCheck,
          accent: '#7c3aed',
          kicker: 'Réflexe 3',
          title: 'Se protéger',
          body:
            "<strong>Casque ECE 22-06</strong> bien attaché, <strong>gants CE</strong>, blouson visible (gilet rétro), chaussures fermées. <em>Ce n'est pas optionnel — c'est la base.</em>",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          kicker: 'Réflexe 4',
          title: 'Refuser la pression',
          body:
            "Aucun client, aucune plateforme, aucun manager n'a le <strong>droit d'exiger</strong> que tu mettes ta vie en danger. <em>Le Code du travail (art. L4121-1) impose à l'employeur d'assurer ta sécurité.</em>",
        },
      ],
      cardsCols: 2,
    },

    {
      part: 'Partie 5 — Pour aller plus loin',
      title: 'Tes droits en tant que livreur deux-roues',
      callouts: [
        {
          icon: Lightbulb,
          accent: '#1d4ed8',
          body:
            "<strong>Droit de retrait</strong> (art. L4131-1 Code du travail) : si tu estimes ta vie en <strong>danger grave et imminent</strong> (météo extrême, état de fatigue, scooter défectueux), tu peux refuser de travailler <strong>sans sanction</strong>. Informe ton employeur ou la plateforme par écrit.",
        },
        {
          icon: ShieldCheck,
          accent: '#16a34a',
          body:
            "<strong>Équipement obligatoire pris en charge</strong> : si tu es salarié, l'employeur doit fournir et entretenir l'EPI (casque conforme, gilet, gants). En statut auto-entrepreneur, l'investissement est à ta charge mais reste <strong>déductible</strong>.",
        },
        {
          icon: AlertTriangle,
          accent: '#cf2031',
          body:
            "<strong>Accident du travail</strong> : tout accident survenu pendant une course (y compris les trajets entre commandes) est un <strong>accident du travail</strong> ouvrant droit à indemnisation. Déclare-le sous 24 h à ton employeur ou à l'URSSAF si tu es indépendant.",
        },
      ],
    },
  ],

  quiz: [
    {
      id: 'q1',
      question: "À 45 km/h, quelle distance parcourt un scooter en 1 seconde ?",
      options: [
        '5 mètres environ',
        'Environ 8 mètres',
        'Plus de 12 mètres',
        '20 mètres',
      ],
      correctIndex: 2,
      explain:
        "À 45 km/h, on parcourt 12,5 m chaque seconde. Une seconde d'inattention (regard sur le GPS, signal sonore d'un message), c'est un piéton non vu ou une voiture qui freine devant et qu'on ne percevra pas à temps.",
    },
    {
      id: 'q2',
      question:
        "Quel est le PREMIER facteur d'accident identifié chez les livreurs deux-roues ?",
      options: [
        "L'alcool ou les stupéfiants",
        'La pression temporelle (délais, rendement)',
        'Les véhicules mal entretenus',
        'Le manque de formation au permis',
      ],
      correctIndex: 1,
      explain:
        "L'ONISR identifie la pression temporelle (délais imposés par les plateformes, rémunération à la course, notes clients) comme premier facteur. Elle pousse à des comportements à risque : non-respect des feux, slalom, vitesse excessive.",
    },
    {
      id: 'q3',
      question:
        "Distance d'arrêt approximative à 45 km/h sur chaussée MOUILLÉE ?",
      options: [
        '~ 15 mètres',
        '~ 27 mètres',
        '35 à 40 mètres',
        'Plus de 60 mètres',
      ],
      correctIndex: 2,
      explain:
        "Sur sol mouillé à 45 km/h, il faut compter 35 à 40 m entre la perception du danger et l'arrêt (1 s de réaction + freinage avec adhérence réduite). Sur sol sec, c'est ~ 27 m. La pluie multiplie quasiment par 1,5 la distance.",
    },
    {
      id: 'q4',
      question:
        "Si la plateforme te met une mauvaise note pour avoir refusé une course par météo dangereuse, peux-tu être sanctionné ?",
      options: [
        'Oui, tu dois accepter toutes les courses',
        "Non — c'est un droit de retrait protégé par la loi",
        'Seulement si tu prouves le danger après coup',
        'Tu peux être désactivé de la plateforme sans recours',
      ],
      correctIndex: 1,
      explain:
        "Article L4131-1 du Code du travail : tout salarié peut refuser une mission s'il estime sa vie en danger grave et imminent, sans sanction. Pour les indépendants, des décisions récentes (Tribunal de Paris, 2024) ont étendu cette protection aux livreurs des plateformes.",
    },
    {
      id: 'q5',
      question:
        "Quel est le réflexe pédagogique le plus efficace selon les moniteurs ECSR ?",
      options: [
        'Lister toutes les sanctions du Code de la route',
        'Faire prendre conscience par le questionnement',
        'Imposer un quiz noté à la fin de la session',
        'Montrer des vidéos chocs d\'accidents',
      ],
      correctIndex: 1,
      explain:
        "Le cours magistral et les vidéos chocs ne fonctionnent quasiment pas avec ce public. La méthode qui change vraiment les comportements : faire verbaliser la pression et les choix (« Pourquoi tu prends ce risque ? Qu'est-ce qui compte le plus ? ») — c'est le cœur de la sensibilisation ECSR.",
    },
  ],

  ctaFinal: {
    kicker: 'Continue ta réflexion',
    title: 'Distractions au guidon : la prochaine étape',
    body:
      "Le téléphone est l'autre grand piège du livreur. Découvre nos analyses sur les distractions et les sanctions encourues.",
    primary: {
      label: 'Téléphone au guidon : sanctions',
      to: '/magazine/telephone-au-guidon-sanctions',
    },
    secondary: {
      label: 'Distances de sécurité moto',
      to: '/magazine/distances-securite-moto',
    },
  },

  // Override mapping ETM (Option C hybride) — public de cet article = livreurs
  // et conducteurs de scooter, sujets liés au comportement du conducteur
  // (fatigue, distractions, pression) → thème L principal.
  etm: {
    primary: 'L',
    secondary: ['D', 'U'],
    label: 'Le conducteur — comportement & fatigue',
    cta: 'Réviser le thème « Le conducteur »',
  },
};
